// Function index:
//   renderMintWorld(host) — the admin "Mint world" panel: a form that kicks
//       a full themed-world mint, plus a polling list of recent mints with
//       live step progress. Polls every 4s while a mint is running.

import { listAdminWorldMints, mintAdminWorld, type AdminWorldMint } from '../../api.js';
import { esc } from './helpers.js';

let pollTimer: ReturnType<typeof setTimeout> | null = null;
// Bumped every time the panel mounts; a poller from a stale mount bails
// as soon as it sees generation move on, instead of relying on
// host.isConnected (which stays true for hosts detached-then-reused by
// the screen router, and doesn't fire synchronously on unmount).
let generation = 0;

export async function renderMintWorld(host: HTMLElement): Promise<void> {
  if (pollTimer) { clearTimeout(pollTimer); pollTimer = null; }
  const gen = ++generation;

  host.innerHTML = `
    <form id="mint-form" style="display:flex; flex-direction:column; gap:6px; max-width:480px;">
      <label>Theme slug <input name="theme" required pattern="[a-z0-9_-]{1,32}" placeholder="scifi"></label>
      <label>World name <input name="name" required maxlength="120" placeholder="The Neon Reach"></label>
      <label>Theme brief <textarea name="brief" required maxlength="2000" rows="3"
        placeholder="One paragraph of voice: what this world feels like."></textarea></label>
      <div style="display:flex; gap:8px;">
        <label style="flex:1">Size <input name="size" type="number" min="12" max="200" value="60"></label>
        <label style="flex:1">Seed <input name="seed" type="number" placeholder="(random)"></label>
      </div>
      <button class="primary" type="submit">Mint world</button>
      <div class="party-meta" id="mint-error"></div>
    </form>
    <div id="mint-list" style="margin-top:12px;"></div>`;

  const form = host.querySelector('#mint-form') as HTMLFormElement;
  const error = host.querySelector('#mint-error') as HTMLElement;
  const list = host.querySelector('#mint-list') as HTMLElement;

  const schedule = (): void => {
    if (pollTimer) clearTimeout(pollTimer);
    pollTimer = setTimeout(() => { void refresh(); }, 4000);
  };

  async function refresh(): Promise<void> {
    if (gen !== generation) return;
    try {
      const { mints } = await listAdminWorldMints();
      if (gen !== generation) return;
      list.innerHTML = mints.length ? mints.map(row).join('') : '<div class="party-meta">no mints yet</div>';
      if (mints.some((m) => m.status === 'running' && !m.stalled)) {
        schedule();
      }
    } catch (err) {
      if (gen !== generation) return;
      list.innerHTML = `<div class="party-meta">could not load mints: ${esc((err as Error).message)}</div>`;
      schedule();
    }
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    error.textContent = '';
    const fd = new FormData(form);
    const seedRaw = String(fd.get('seed') ?? '').trim();
    try {
      await mintAdminWorld({
        theme: String(fd.get('theme')),
        name: String(fd.get('name')),
        brief: String(fd.get('brief')),
        size: Number(fd.get('size')) || 60,
        ...(seedRaw ? { seed: Number(seedRaw) } : {}),
      });
      form.reset();
      await refresh();
    } catch (err) {
      error.textContent = (err as Error).message;
    }
  });

  await refresh();
}

function row(m: AdminWorldMint): string {
  const badge = m.status === 'done' ? '✅' : m.status === 'failed' ? '❌' : m.stalled ? '⚠️ stalled' : '⏳';
  const detail = m.status === 'failed' ? esc(m.error ?? '') : esc(m.step);
  return `<div style="display:flex; gap:8px; align-items:baseline; margin:3px 0;">
    <span>${badge}</span><strong>${esc(m.theme)}</strong>
    <span class="party-meta" style="flex:1">${detail}</span>
    ${m.world_id != null ? `<span class="party-meta">world ${m.world_id}</span>` : ''}
  </div>`;
}
